/* ─────────────────────────  THE ONE TIME MODULE  ─────────────────────────────
 *
 * Every zone, day key and clock label in the frontend comes from here
 * (BUILDING_TIME_PLAN.md R-T8, Plan C WP-C1). It is the twin of
 * lobbero_be/src/desk-day.js, on Intl instead of moment.
 *
 * THE RULE IT SERVES (R-T12): the clock depends on who is reading.
 *   - a concierge and a resident are on their BUILDING's clock;
 *   - an owner and a property manager are on their own DEVICE's clock, one
 *     clock across every building they hold; the Super Admin console likewise.
 * The server has already applied that rule to everything it answers and says
 * so (`tz`, `tz_source`). So a screen formats in the zone its response carries
 * and never decides for itself — resolveClock() below is the one place the
 * precedence (surface, then response, then role) is written down.
 *
 * WHAT MAY NOT LIVE ANYWHERE ELSE (the source scan in building-time.guard.test
 * fails on it): reading the device's zone, toLocaleDateString /
 * toLocaleTimeString, an ISO string sliced into a date.
 *
 * ONE MISSING-ZONE RULE. A time with no usable zone is DISPLAYED on the
 * device's clock, and the label beside it says so (clockNote). It used to be
 * the device's clock in one helper and UTC in another. The one exception is
 * arithmetic that mirrors the server: safeZone() answers 'UTC', because that
 * is what the server's lb_safe_timezone computes a pass window in.
 */

import { DATE_LOCALE } from './locale';

/* ─────────────────────────────────  Zones  ───────────────────────────────── */

/** The header the server reads the device's zone from (src/desk-day.js). */
export const CLIENT_ZONE_HEADER = 'X-Client-Timezone';

/** The header that tells the server the reader is standing at the DESK, where
 *  every time and every day boundary is the building's, whoever is reading
 *  (src/desk-day.js, audienceOf). Sent from desk screens only. */
export const CLOCK_SURFACE_HEADER = 'X-Clock-Surface';

/**
 * THE DEVICE'S ZONE — the only place in the app that asks the platform for it.
 * Read per call, never cached: a laptop changes zone when it travels. '' when
 * the platform will not say.
 */
export const deviceZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const knownZones = new Map<string, boolean>();

/** The zone as given when Intl can compute in it, '' otherwise. */
export const usableZone = (zone: string | null | undefined): string => {
  if (typeof zone !== 'string') return '';
  const name = zone.trim();
  if (!name || name.length > 64) return '';
  let known = knownZones.get(name);
  if (known === undefined) {
    try {
      new Intl.DateTimeFormat(DATE_LOCALE, { timeZone: name }).format(0);
      known = true;
    } catch {
      known = false;
    }
    knownZones.set(name, known);
  }

  return known ? name : '';
};

/** The zone, or 'UTC' — the server's lb_safe_timezone rule. For ARITHMETIC that
 *  mirrors the server (pass windows). Display uses displayZone(). */
export const safeZone = (zone: string | null | undefined): string =>
  usableZone(zone) || 'UTC';

/** The zone a time is DISPLAYED in when the one asked for may be missing: the
 *  zone, else the device's, else UTC. Whoever calls this labels the result. */
export const displayZone = (zone: string | null | undefined): string =>
  usableZone(zone) || usableZone(deviceZone()) || 'UTC';

/* Names a browser still reports for a zone the server spells the modern way.
   Only the CITY label needs them; arithmetic is by offset. */
const CITY_ALIASES: Record<string, string> = {
  Calcutta: 'Kolkata',
  Kiev: 'Kyiv',
  Saigon: 'Ho Chi Minh',
  Katmandu: 'Kathmandu',
  Rangoon: 'Yangon',
};

/**
 * A ZONE, NAMED FOR A PERSON — the city, exactly as the server prints it
 * (zoneCity in src/desk-day.js): "Toronto" for America/Toronto, "Buenos Aires"
 * for America/Argentina/Buenos_Aires, "UTC" for UTC and the Etc family.
 *
 * '' for a missing or unusable zone: the formatters then fell back to another
 * clock, and printing a name the time was not rendered in is worse than
 * printing none.
 */
export const zoneLabel = (zone: string | null | undefined): string => {
  const name = usableZone(zone);
  if (!name) return '';
  if (name === 'UTC' || name === 'GMT' || /^Etc\//.test(name)) return 'UTC';
  const city = (name.split('/').pop() || name).replace(/_/g, ' ');

  return CITY_ALIASES[city] || city;
};

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

const partsFormatter = (zone: string): Intl.DateTimeFormat => {
  let formatter = partsFormatters.get(zone);
  if (!formatter) {
    // hourCycle h23, never hour12:false — the latter may print midnight as 24,
    // which would put every boundary a day late.
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatters.set(zone, formatter);
  }

  return formatter;
};

/** The wall-clock fields of an instant in a zone. Null for a bad instant or a
 *  zone Intl rejects. */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export const wallClockIn = (
  instant: Date | number | string,
  zone: string
): WallClock | null => {
  const ms = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
  if (Number.isNaN(ms)) return null;
  try {
    const parts = partsFormatter(zone).formatToParts(new Date(ms));
    const at = (type: string): number =>
      Number(parts.find(part => part.type === type)?.value);
    const wall = {
      year: at('year'),
      month: at('month'),
      day: at('day'),
      hour: at('hour'),
      minute: at('minute'),
      second: at('second'),
    };

    return Object.values(wall).some(Number.isNaN) ? null : wall;
  } catch {
    return null;
  }
};

/**
 * How far a zone's wall clock is ahead of UTC at an instant, in ms. 0 for UTC
 * and for a zone Intl rejects.
 *
 * MEASURED ON A WHOLE SECOND: formatToParts has no millisecond part, so
 * measuring against the raw instant folds the input's own milliseconds into the
 * "offset" (the end of a day, 23:59:59.999, once landed on ...59.997Z).
 */
export const offsetAt = (ms: number, zone: string): number => {
  if (zone === 'UTC') return 0;
  const whole = Math.floor(ms / 1000) * 1000;
  const wall = wallClockIn(whole, zone);
  if (!wall) return 0;

  return (
    Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second) -
    whole
  );
};

/** "UTC-04:00" / "UTC+05:30" / "UTC+00:00" at an instant — the server's
 *  moment 'Z' form with the prefix it prints. */
export const zoneOffsetLabel = (
  zone: string | null | undefined,
  at: Date | number = Date.now()
): string => {
  const ms = at instanceof Date ? at.getTime() : at;
  const minutes = Math.round(offsetAt(ms, safeZone(zone)) / 60000);
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const pad = (n: number): string => String(n).padStart(2, '0');

  return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

/** "Toronto time (UTC-04:00)" — the server's zoneNote. "UTC" for UTC, '' for a
 *  zone that cannot be named. Print a label the SERVER sent in preference. */
export const zoneNote = (
  zone: string | null | undefined,
  at: Date | number = Date.now()
): string => {
  const city = zoneLabel(zone);
  if (!city) return '';
  if (city === 'UTC') return 'UTC';

  return `${city} time (${zoneOffsetLabel(zone, at)})`;
};

/**
 * Are two zones the same CLOCK at an instant? By offset, never by name: a
 * browser still reports Asia/Calcutta and Europe/Kiev for zones the server
 * calls Asia/Kolkata and Europe/Kyiv. A missing or unusable zone equals nothing.
 */
export const sameClock = (
  a: string | null | undefined,
  b: string | null | undefined,
  at: Date | number = Date.now()
): boolean => {
  const first = usableZone(a);
  const second = usableZone(b);
  if (!first || !second) return false;
  if (first === second) return true;
  const ms = at instanceof Date ? at.getTime() : at;

  return offsetAt(ms, first) === offsetAt(ms, second);
};

/* ────────────────────────────────  Day keys  ─────────────────────────────── */

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day spelled 'YYYY-MM-DD'. */
export const isDayKey = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const m = DAY_KEY.exec(value);
  if (!m) return false;
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));

  return (
    at.getUTCFullYear() === Number(m[1]) &&
    at.getUTCMonth() === Number(m[2]) - 1 &&
    at.getUTCDate() === Number(m[3])
  );
};

const keyOf = (year: number, month: number, day: number): string => {
  const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
};

/**
 * The calendar day ('YYYY-MM-DD') an instant falls on in a zone. A missing or
 * unusable zone is the device's day (displayZone). '' for a bad instant.
 */
export const dayKeyIn = (
  instant: Date | number | string,
  zone?: string | null
): string => {
  const wall = wallClockIn(instant, displayZone(zone));

  return wall ? keyOf(wall.year, wall.month, wall.day) : '';
};

/** Today in a zone. */
export const todayIn = (zone?: string | null, now: Date = new Date()): string =>
  dayKeyIn(now, zone);

/** A day key moved by whole calendar days — by calendar arithmetic, never by
 *  adding 24 hours, which lands on the wrong day across a clock change. */
export const addDays = (key: string, days: number): string => {
  const m = DAY_KEY.exec(key);
  if (!m) return key;
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));

  return keyOf(at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate());
};

/** Whole days from one key to another (b − a). NaN when either is not a key. */
export const daysBetween = (a: string, b: string): number => {
  const first = DAY_KEY.exec(a);
  const second = DAY_KEY.exec(b);
  if (!first || !second) return NaN;

  return Math.round(
    (Date.UTC(Number(second[1]), Number(second[2]) - 1, Number(second[3])) -
      Date.UTC(Number(first[1]), Number(first[2]) - 1, Number(first[3]))) /
      86400000
  );
};

/** The first day of a key's month, and the first day of its year. */
export const monthStartOf = (key: string): string =>
  DAY_KEY.test(key) ? `${key.slice(0, 7)}-01` : key;

export const yearStartOf = (key: string): string =>
  DAY_KEY.test(key) ? `${key.slice(0, 4)}-01-01` : key;

/** The first day of the quarter a key is in. */
export const quarterStartOf = (key: string): string => {
  const m = DAY_KEY.exec(key);
  if (!m) return key;
  const month = Math.floor((Number(m[2]) - 1) / 3) * 3 + 1;

  return keyOf(Number(m[1]), month, 1);
};

/** A day key moved by whole months, clamped to the month's last day. */
export const addMonths = (key: string, months: number): string => {
  const m = DAY_KEY.exec(key);
  if (!m) return key;
  const first = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + months, 1));
  const last = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)
  ).getUTCDate();

  return keyOf(
    first.getUTCFullYear(),
    first.getUTCMonth() + 1,
    Math.min(Number(m[3]), last)
  );
};

/** The desk day starts at 07:00 in the building zone (BE src/desk-day.js). */
export const DESK_DAY_START_HOUR = 7;

/**
 * The building's DESK date: its calendar date, or the day before when its wall
 * clock is before 07:00 — decided on the wall-clock HOUR, as the server's
 * deskDayKey is, so the hour a clock changes cannot file under the wrong day.
 */
export const deskDayOf = (zone?: string | null, now: Date = new Date()): string => {
  const wall = wallClockIn(now, displayZone(zone));
  if (!wall) return '';
  const today = keyOf(wall.year, wall.month, wall.day);

  return wall.hour < DESK_DAY_START_HOUR ? addDays(today, -1) : today;
};

/** The building's CALENDAR date — what "today" means for every date rule. */
export const calendarDayOf = (zone?: string | null, now: Date = new Date()): string =>
  dayKeyIn(now, zone);

/* ─────────────────────────  Wall clock → instant  ────────────────────────── */

/**
 * `2026-08-11` + `00:00:00.000` in `Europe/Sofia` -> the ISO instant.
 *
 * There is no Intl API for "the instant of this local time in that zone", so it
 * is measured: take the wall time as if it were UTC, see how far the zone is
 * from UTC there, shift by that much — and measure AGAIN, because the offset
 * can differ either side of a clock change and one pass lands an hour out on
 * exactly the two days a year when a boundary matters most.
 *
 * Undefined for an empty or malformed date. A missing or unusable zone reads
 * the wall time on the device's clock; the CALLER says which clock was used.
 */
export const zonedIso = (
  date: string,
  timeOfDay: string,
  tz?: string | null
): string | undefined => {
  if (!date) return undefined;

  const wallAsUtc = new Date(`${date}T${timeOfDay}Z`);
  if (Number.isNaN(wallAsUtc.getTime())) return undefined;
  const zone = displayZone(tz);
  const first = offsetAt(wallAsUtc.getTime(), zone);
  let instant = new Date(wallAsUtc.getTime() - first);
  const second = offsetAt(instant.getTime(), zone);
  if (second !== first) {
    instant = new Date(wallAsUtc.getTime() - second);
  }

  return instant.toISOString();
};

/** The first instant of a calendar day in a zone. */
export const zonedDayStartIso = (
  date: string,
  tz?: string | null
): string | undefined => zonedIso(date, '00:00:00.000', tz);

/** The LAST instant of a calendar day in a zone — 23:59:59.999, so a range
 *  ending on the 11th includes the 11th. */
export const zonedDayEndIso = (
  date: string,
  tz?: string | null
): string | undefined => zonedIso(date, '23:59:59.999', tz);

/** The value an `<input type="datetime-local">` wants for an instant, as the
 *  wall clock of a zone: 'YYYY-MM-DDTHH:mm'. */
export const toZonedInputValue = (
  instant: Date | number | string,
  zone?: string | null
): string => {
  const wall = wallClockIn(instant, displayZone(zone));
  if (!wall) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');

  return `${keyOf(wall.year, wall.month, wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
};

/** A `datetime-local` value ('YYYY-MM-DDTHH:mm') read as the wall clock of a
 *  zone -> the ISO instant. Undefined when it is not one. */
export const zonedInputToIso = (
  value: string,
  zone?: string | null
): string | undefined => {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2})?/.exec(value || '');
  if (!m) return undefined;

  return zonedIso(m[1], `${m[2]}${m[3] || ':00'}.000`, zone);
};

/* ───────────────────────────────  Formatting  ────────────────────────────── */

/**
 * An instant as text in a zone, under the product locale. '' for nothing or a
 * bad instant. The one formatter every helper below — and every screen — goes
 * through; a missing or unusable zone is the device's clock (displayZone).
 */
export const formatIn = (
  instant: Date | number | string | null | undefined,
  zone: string | null | undefined,
  options: Intl.DateTimeFormatOptions
): string => {
  if (instant === null || instant === undefined || instant === '') return '';
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(DATE_LOCALE, {
      ...options,
      timeZone: displayZone(zone),
    }).format(at);
  } catch {
    return '';
  }
};

const CLOCK: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

/** HH:mm in a zone. Empty in, empty out — several of these fields are
 *  legitimately null (an open shift has no ended_at). */
export const clockTime = (iso: string | null | undefined, tz?: string | null): string =>
  formatIn(iso, tz, CLOCK);

/** HH:mm:ss in a zone. */
export const clockTimeSeconds = (
  iso: string | null | undefined,
  tz?: string | null
): string => formatIn(iso, tz, { ...CLOCK, second: '2-digit' });

/** "07 Oct, 14:05" — day and time, for a span that may cross midnight. */
export const clockDayTime = (
  iso: string | null | undefined,
  tz?: string | null
): string =>
  formatIn(iso, tz, { day: '2-digit', month: 'short', ...CLOCK });

/** "7 Oct 2026" in a zone. */
export const dateIn = (
  iso: string | null | undefined,
  tz?: string | null,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
): string => formatIn(iso, tz, options);

/** "7 Oct 2026, 14:05" in a zone. */
export const dateTimeIn = (iso: string | null | undefined, tz?: string | null): string =>
  formatIn(iso, tz, { day: 'numeric', month: 'short', year: 'numeric', ...CLOCK });

/**
 * 'YYYY-MM-DD HH:mm' of an instant in a zone — the shape the server's own
 * `arrived_local` text has, for a desk screen that must print the BUILDING's
 * clock even when the server answered its reader on another one.
 */
export const wallTextIn = (
  instant: Date | number | string | null | undefined,
  zone?: string | null,
  { seconds = false }: { seconds?: boolean } = {}
): string => {
  if (instant === null || instant === undefined || instant === '') return '';
  const wall = wallClockIn(instant, displayZone(zone));
  if (!wall) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');

  return (
    `${keyOf(wall.year, wall.month, wall.day)} ${pad(wall.hour)}:${pad(wall.minute)}` +
    (seconds ? `:${pad(wall.second)}` : '')
  );
};

/**
 * WHEN SOMEBODY CAME IN, AS TEXT ON A GIVEN CLOCK. The server sends both the
 * instant (`arrived_at`) and its own rendering (`arrived_local`, on the clock
 * it answered the READER on). A desk screen is the building's clock for
 * whoever is at it, so it renders from the instant; the server's text is the
 * fallback for a row without one.
 */
export const arrivedText = (
  row: { arrived_at?: string | null; arrived_local?: string | null },
  zone?: string | null
): string => wallTextIn(row.arrived_at, zone) || row.arrived_local || '';

/**
 * A day KEY as a date ("7 Oct", or with the year). A key is already a calendar
 * day on somebody's clock, so it is printed as it is and never re-zoned —
 * passing it through a zone conversion shifts the label off its bucket.
 */
export const formatDayKey = (
  key: string,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
): string => {
  const m = DAY_KEY.exec(key);
  if (!m) return key;
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));

  return new Intl.DateTimeFormat(DATE_LOCALE, { ...options, timeZone: 'UTC' }).format(at);
};

/**
 * A date-ONLY value from the API ('YYYY-MM-DD', or an ISO string whose date
 * part is the fact) as a date. Never through `new Date()`: that reads a bare
 * date as UTC midnight and prints the day before west of Greenwich.
 */
export const formatDateOnly = (
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
): string => {
  if (!value) return '';
  const key = String(value).slice(0, 10);

  return isDayKey(key) ? formatDayKey(key, options) : '';
};

/**
 * The server's zone-less wall-clock TEXT ('YYYY-MM-DD HH:mm:ss' — a Records
 * row's `createdAt`), printed as it is: it is ALREADY on the response's clock.
 * Never through `new Date()` — that re-reads it on the device's clock, and is
 * Invalid Date in WebKit.
 */
export const formatWallText = (
  text: string | null | undefined,
  { seconds = false }: { seconds?: boolean } = {}
): string => {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(text || '');
  if (!m || !isDayKey(m[1])) return text || '';
  const day = formatDayKey(m[1], { day: 'numeric', month: 'short', year: 'numeric' });

  return `${day}, ${m[2]}:${m[3]}${seconds && m[4] ? `:${m[4]}` : ''}`;
};

/* ──────────────────────────  Who is on which clock  ──────────────────────── */

/** Where a screen is drawn. The desk shell and the resident portal are
 *  building-clock surfaces for whoever is in them; the Super Admin console is
 *  the reader's; everything else follows its data. */
export type ClockSurface = 'desk' | 'portal' | 'console' | 'app';

/**
 * The surface a PATH belongs to — for what is mounted above every shell (the
 * IQ chat at the App root, the request interceptor) and has only the address
 * to go by. Everything inside a shell reads the context instead.
 */
export const surfaceOfPath = (pathname: string): ClockSurface => {
  const path = pathname || '';
  if (path === '/admin' || path.startsWith('/admin/')) return 'console';
  if (path === '/portal' || path.startsWith('/portal/')) return 'portal';
  if (
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path.startsWith('/account') ||
    path.startsWith('/record/') ||
    path.startsWith('/onboarding') ||
    path === '/'
  ) {
    return 'app';
  }

  // Every other signed-in path is a desk screen (main menu, the two logs, the
  // exit desks, parcels, roster, the handover page) — and a hotel's
  // /reception (Independent Stays IS9, F-3): it is on the property's clock.
  return 'desk';
};

/** How the server chose a response's zone. NOT a building's timezone_source. */
export type TzSource = 'viewer' | 'building' | 'fallback';

/** The two fields every zone-decided response carries. */
export interface ClockFields {
  tz?: string | null;
  tz_source?: TzSource | string | null;
}

/** A clock a screen formats in, and what to call it. */
export interface Clock {
  /** The zone every time on the screen is formatted in. Always usable. */
  zone: string;
  /** 'yours' = the reader's device; 'building' = the building's own. */
  whose: 'yours' | 'building';
  /**
   * Set when the clock is a stand-in for a building's missing zone:
   *   'device' — a building-clock screen showing the device's time;
   *   'utc'    — nothing usable at all.
   */
  missing: null | 'device' | 'utc';
}

export interface ClockInput {
  surface?: ClockSurface;
  /** The response on screen, when there is one. */
  response?: ClockFields | null;
  /** users.role — a staff capability or null. */
  role?: string | null;
  /** The zone of the building the screen is about, when known. */
  buildingZone?: string | null;
  /** Injected in tests; the device's own otherwise. */
  device?: string;
}

const isViewerRole = (role: string | null | undefined): boolean => {
  const value = String(role || '')
    .trim()
    .toLowerCase();

  return (
    value === 'owner' ||
    value === 'property_manager' ||
    value === 'admin' ||
    value === 'manager'
  );
};

const stoodIn = (device: string, source?: string | null): Clock =>
  device && source !== 'fallback'
    ? { zone: device, whose: 'yours', missing: 'device' }
    : { zone: 'UTC', whose: 'building', missing: 'utc' };

/**
 * WHOSE CLOCK A SCREEN IS ON. The precedence is decided (PLAN-C-KICKOFF §3):
 *
 *   1. SURFACE. On the desk and in the resident portal every time is the
 *      BUILDING's, whatever `tz_source` the response carries — an owner who
 *      opens the desk is answered `viewer` by the shared endpoints and still
 *      reads building time. The console is always the reader's.
 *   2. RESPONSE. Anywhere else a screen formats in the `tz` its response
 *      carries and names it from `tz_source`. The server has applied the rule;
 *      a screen never disagrees with the data it shows.
 *   3. ROLE. With no response (a live clock, an input, a "now" marker): owner
 *      and property manager on the device's clock, everybody else the
 *      building's.
 *
 * A building with no usable zone: the server answers the device's zone
 * (`viewer`), or UTC (`fallback`) when there is no device zone either. A
 * building-clock reader is then told so (`missing`) — never silently shown
 * their own clock as if it were the building's.
 */
export const resolveClock = (input: ClockInput = {}): Clock => {
  const surface = input.surface || 'app';
  const device = usableZone(input.device === undefined ? deviceZone() : input.device);
  const building = usableZone(input.buildingZone);
  const response = input.response || null;
  const answered = usableZone(response?.tz);
  const source = response?.tz_source || null;

  if (surface === 'console') {
    return device
      ? { zone: device, whose: 'yours', missing: null }
      : { zone: 'UTC', whose: 'yours', missing: 'utc' };
  }

  if (surface === 'desk' || surface === 'portal') {
    if (building) return { zone: building, whose: 'building', missing: null };
    // A response that says `building` IS the building's zone, even when the
    // caller had no building to look one up from.
    if (answered && source === 'building') {
      return { zone: answered, whose: 'building', missing: null };
    }

    return stoodIn(device, source);
  }

  if (answered) {
    if (source === 'fallback') return { zone: 'UTC', whose: 'building', missing: 'utc' };
    if (source === 'building') return { zone: answered, whose: 'building', missing: null };
    // `viewer`: the reader's own clock — unless the reader is somebody who is
    // on a building's clock by rule, in which case the building had none.
    if (source === 'viewer' && !isViewerRole(input.role)) {
      return { zone: answered, whose: 'yours', missing: 'device' };
    }

    return { zone: answered, whose: 'yours', missing: null };
  }

  if (isViewerRole(input.role)) {
    if (device) return { zone: device, whose: 'yours', missing: null };
    if (building) return { zone: building, whose: 'building', missing: null };

    return { zone: 'UTC', whose: 'yours', missing: 'utc' };
  }

  return building
    ? { zone: building, whose: 'building', missing: null }
    : stoodIn(device, source);
};

/**
 * THE LINE EVERY SCREEN WITH A TIME ON IT CARRIES: "Your time — Budapest" or
 * "Building time — Toronto". `label` is a zone label the SERVER sent
 * (X-Report-Timezone-Label, timezone_label) and is printed verbatim. `place`
 * is 'Property' in an organisation without Desk (Independent Stays IS2,
 * stays/wording.ts) — "Property time — Lisbon".
 */
export const clockNote = (clock: Clock, label?: string | null, place: 'Building' | 'Property' = 'Building'): string => {
  if (clock.missing === 'utc') return 'UTC';
  if (clock.missing === 'device') {
    return `Your device's time — this ${place.toLowerCase()} has no time zone yet`;
  }
  const name = (label && label.trim()) || zoneLabel(clock.zone) || clock.zone;

  return clock.whose === 'yours' ? `Your time — ${name}` : `${place} time — ${name}`;
};

/**
 * THE SECOND LINE OF A RECORD'S DETAIL: "20:05 your time · 14:05 in Toronto".
 * '' when the reader is already on the building's clock at that instant (a
 * concierge; a manager in the same zone) or the building has no usable zone —
 * one line is then the whole truth.
 */
export const bothClocks = (
  iso: string | null | undefined,
  clock: Clock,
  buildingZone: string | null | undefined
): string => {
  if (!iso) return '';
  const at = new Date(iso);
  const building = usableZone(buildingZone);
  if (Number.isNaN(at.getTime()) || !building) return '';
  if (clock.whose !== 'yours' || sameClock(clock.zone, building, at)) return '';

  return `${clockTime(iso, clock.zone)} your time · ${clockTime(iso, building)} in ${zoneLabel(building)}`;
};

/**
 * THE CLOCK LINE OF A BUILDING RECORD — a handover, a shift summary, a sent
 * report. Those are on the building's clock WHOEVER reads them (R-T12: nobody
 * was reading when they were written), so they say "Building time — Sofia"
 * even on an owner's dashboard, beside an answer that says "Your time — …".
 */
export const buildingClockNote = (zone: string | null | undefined): string =>
  clockNote(resolveClock({ surface: 'desk', buildingZone: zone }));

/**
 * "Thu 06 Mar 22:00 – Fri 07 Mar 06:04 · Building time — Sofia" — a building
 * record's window, in the building's zone, with its clock named. `withDate`
 * adds the day of the month for a window that may be days old.
 */
export const buildingWindowLabel = (
  from: string,
  to: string,
  zone: string | null | undefined,
  { withDate = false }: { withDate?: boolean } = {}
): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    ...(withDate ? { day: '2-digit', month: 'short' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  return `${formatIn(from, zone, options)} – ${formatIn(to, zone, options)} · ${buildingClockNote(
    zone
  )}`;
};

/* ───────────────────────────────  Elapsed time  ──────────────────────────── */

/** Elapsed time as "4h 12m" / "12m" / "just now".
 *
 *  Rounded DOWN to the minute deliberately. An attendance figure that rounds up
 *  reports more time than the record supports, and the direction of the error is
 *  the whole point on a screen a manager might read as a timesheet. */
export const elapsed = (fromIso: string, toIso?: string): string => {
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  if (Number.isNaN(from) || Number.isNaN(to)) return '';
  const mins = Math.floor(Math.max(0, to - from) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);

  return `${h}h ${mins % 60}m`;
};

/** "4 min ago" — THE ONLY HONEST RENDERING OF last_used_at.
 *
 *  The backend says so in writing (lobbero_be/src/CRUD/shifts.js): the liveness
 *  window is 15 minutes because last_used_at is only rewritten every 5 minutes
 *  and the access token lives 15, so somebody who shut a laptop ten minutes ago
 *  is still inside the window. A green dot would state "online" about that
 *  person. An age states what is actually known. Never replace this with a dot. */
export const ageLabel = (iso: string | null | undefined): string => {
  if (!iso) return 'no recent activity';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'no recent activity';
  const mins = Math.floor(Math.max(0, Date.now() - t) / 60000);
  if (mins < 1) return 'active under a minute ago';
  if (mins === 1) return 'active 1 min ago';
  if (mins < 60) return `active ${mins} min ago`;
  const h = Math.floor(mins / 60);
  if (h === 1) return 'active 1 hr ago';

  return `active ${h} hrs ago`;
};
